/**
 * Real measure-and-fit text sizing, replacing the old fixed-ratio scaling
 * formula (scaledLayerFontSize/scaledCtaFontSize). Given a box, some text,
 * and a way to measure that text's rendered width, finds the largest font
 * size that actually fits - growing on large boxes, shrinking on small
 * ones, rather than only ever scaling up from a hardcoded reference width.
 *
 * The measureText callback is what lets this run identically in two very
 * different environments: the browser has real Canvas measureText (exact);
 * Node has no Canvas/DOM at all, so it gets a character-width heuristic
 * instead (same approach gif.js's own wrapLines already used, just now
 * shaped to this shared interface). The two aren't pixel-identical, but
 * they're both genuine best-effort fits rather than one being a guess.
 */

/** Greedy word-wrap: text → lines that fit boxW at fontSize, per measureText. */
function wrapForFit(text, boxW, fontSize, measureText) {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];
  const lines = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line === "" || measureText(candidate, fontSize) <= boxW) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Largest font size (within [minFontSize, maxFontSize]) at which `text`,
 * word-wrapped to boxW, fits within boxH - for multi-line blocks
 * (headline/subline/custom text). Binary search over integer font sizes;
 * O(log(maxFontSize)) wrap attempts, cheap enough to recompute on every
 * drag frame.
 */
export function fitBlockFontSize(text, boxW, boxH, opts = {}) {
  const {
    measureText,
    minFontSize = 8,
    maxFontSize = 200,
    lineHeightRatio = 1.15,
    fillRatio = 0.92,
  } = opts;

  if (!text || !Number.isFinite(boxW) || !Number.isFinite(boxH) || boxW <= 0 || boxH <= 0) {
    return minFontSize;
  }

  let lo = minFontSize;
  let hi = maxFontSize;
  let best = minFontSize;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const lines = wrapForFit(text, boxW, mid, measureText);
    const blockH = lines.length * mid * lineHeightRatio;
    const fitsHeight = blockH <= boxH * fillRatio;
    // wrapForFit only breaks at word boundaries - a single word (or any
    // line with nowhere left to break) gets accepted as "one line" even
    // when it's wider than boxW on its own. Without this check, a tall,
    // narrow box with short text (little height pressure) lets the font
    // grow arbitrarily large while overflowing horizontally the whole time.
    const widestLine = lines.reduce((max, ln) => Math.max(max, measureText(ln, mid)), 0);
    const fitsWidth = widestLine <= boxW;
    if (fitsHeight && fitsWidth) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/**
 * Largest font size at which single-line `text` fits within the box - for
 * compact, non-wrapping elements (CTA buttons). Checks both the rendered
 * text width against boxW and the font size itself against boxH, so a
 * wide-but-short box and a narrow-but-tall box are both handled correctly.
 */
export function fitInlineFontSize(text, boxW, boxH, opts = {}) {
  const {
    measureText,
    minFontSize = 8,
    maxFontSize = 200,
    fillRatioH = 0.55,
    paddingW,
  } = opts;

  if (!text || !Number.isFinite(boxW) || !Number.isFinite(boxH) || boxW <= 0 || boxH <= 0) {
    return minFontSize;
  }

  // Proportional to box width rather than a fixed pixel value - a flat
  // 16px reads as reasonable padding on a small CTA but becomes visually
  // insignificant on a large one. At least 16px, or 10% of the box
  // width, whichever is larger.
  const effectivePaddingW = paddingW ?? Math.max(16, boxW * 0.1);

  let lo = minFontSize;
  let hi = maxFontSize;
  let best = minFontSize;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const w = measureText(text, mid);
    const fitsW = w <= Math.max(0, boxW - effectivePaddingW);
    const fitsH = mid <= boxH * fillRatioH;
    if (fitsW && fitsH) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/**
 * Character-width heuristic measureText for environments without a real
 * Canvas (Node/GIF export). Same rough advance-width-per-character
 * approach as gif.js's original wrapLines, generalized to the
 * measureText(text, fontSize) => px shape the fit functions expect.
 */
export function heuristicMeasureText(text, fontSize) {
  return text.length * fontSize * 0.55;
}

/**
 * Proportional CTA border-radius - matches the same "scale with the box,
 * not a flat constant" principle font-size already uses. A flat radius
 * (e.g. 8px) looks right at one particular CTA size and increasingly
 * subtle/square on much larger ones (a big social-size button next to a
 * small display one), since it doesn't grow with the button at all.
 * ~6px at a "typical" ~38-40px-tall CTA (matching the earlier flat value
 * this replaces), scaling from 3px on tiny buttons up to a 16px ceiling
 * so very tall buttons don't turn into a full pill shape.
 */
export function ctaBorderRadius(boxH) {
  if (!Number.isFinite(boxH)) return 6;
  return Math.max(3, Math.min(16, Math.round(boxH * 0.15)));
}